import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { sitePageUrl } from "@/app/apiBase";
import type { Scenario, Subscription, ThemeSetting } from "@/domain/entities";
import { LOCALES, LOCALE_NAMES, type Locale } from "@/domain/localized";
import { AccountSection } from "../components/account/AccountSection";
import { RemindersSection } from "../components/reminders/RemindersSection";
import { TransferSection } from "../components/transfer/TransferSection";
import { SignInSection } from "../components/account/SignInSection";
import { Button } from "../components/Button";
import { ScenarioPicker } from "../components/ScenarioPicker";
import { Screen } from "../components/Screen";
import { useApp } from "../container";
import { useQuery } from "../hooks/useQuery";
import { useSettings } from "../hooks/useSettings";
import { useLocale } from "../i18n/LocaleProvider";
import { MedicinesEditor } from "./MedicinesEditor";
import styles from "./SettingsScreen.module.css";

export function SettingsScreen() {
  const app = useApp();
  const { t, pick, locale, setLocale } = useLocale();
  const settings = useSettings();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [handoffFailed, setHandoffFailed] = useState(false);
  const reminderState = useQuery(() => app.reminders.state(), []);

  // Coming back from an identity provider: the URL carries a one-time code or an error.
  const handoff = searchParams.get("auth");
  const handoffProvider = searchParams.get("provider") ?? "oauth";
  const oauthError = searchParams.get("auth_error");
  const authError = oauthError === "email_required" ? t.account.errors.emailRequired : oauthError || handoffFailed ? t.account.errors.authFailed : null;
  useEffect(() => {
    if (!handoff) return;
    void app.account
      .completeHandoff(handoff)
      .then(() => {
        app.analytics.track("sign_in", { provider: handoffProvider });
        setSearchParams({}, { replace: true });
      })
      .catch(() => setHandoffFailed(true));
    // Runs once per code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff]);

  const data = useQuery(async () => {
    const [subscriptions, scenarios] = await Promise.all([app.store.subscriptions.listAll(), app.store.scenarios.list()]);
    return { subscriptions, scenarios };
  }, [], app.changeSources);

  const scenarios = data.data?.scenarios ?? [];
  const subscriptions = data.data?.subscriptions ?? [];
  const scenarioOf = (s: Subscription): Scenario | undefined => scenarios.find((x) => x.id === s.scenarioId);
  const localeValue = settings.data?.locale ?? "system";
  // The iOS pickers take the app's language at launch; say so until the app has restarted with the choice.
  const restartForPickers = useQuery(() => app.systemLanguage.needsRestart(settings.data?.locale), [localeValue]);

  return (
    <Screen title={t.settings.title}>
      <RemindersSection state={reminderState.data} reload={reminderState.reload} />
      <section className={styles.section}>
        <h2 className={styles.heading}>{t.settings.scenarios}</h2>
        {subscriptions.map((subscription) => {
          const scenario = scenarioOf(subscription);
          if (!scenario) return null;
          return (
            <div key={subscription.id} className={styles.card}>
              <div className={styles.name}>
                <span>{pick(scenario.name)}</span>
                {!subscription.enabled && <span className={styles.muted}>{t.settings.paused}</span>}
              </div>
              <label className={styles.cardRow}>
                {t.settings.reminderAt}
                <input
                  className={styles.field}
                  style={{ width: "auto" }}
                  type="time"
                  value={subscription.reminderTime}
                  onChange={(e) => app.subscriptions.update(subscription.id, { reminderTime: e.target.value })}
                />
              </label>
              {scenario.settingsSchema?.medications && (
                <MedicinesEditor
                  saved={subscription.settings.medications ?? []}
                  onSave={(medications) => app.subscriptions.update(subscription.id, { settings: { ...subscription.settings, medications } }).then(() => undefined)}
                />
              )}
              <div className={styles.subsection}>
                {!subscription.enabled && <p className={styles.muted}>{t.settings.pausedHint}</p>}
                <div className={styles.actions}>
                  <Button onClick={() => void app.subscriptions.update(subscription.id, { enabled: !subscription.enabled }).then(() => app.reminders.sync())}>
                    {subscription.enabled ? t.settings.pause : t.settings.resume}
                  </Button>
                </div>
                {confirming === subscription.id ? (
                  <div className={styles.actions}>
                    <span className={styles.muted}>{t.settings.removeConfirm}</span>
                    <Button variant="danger" onClick={() => { app.analytics.track("scenario_removed", { scenarioId: scenario.slug }); void app.subscriptions.unsubscribe(subscription.id).then(() => { setConfirming(null); return app.reminders.sync(); }); }}>{t.settings.removeYes}</Button>
                    <Button variant="ghost" onClick={() => setConfirming(null)}>{t.common.cancel}</Button>
                  </div>
                ) : (
                  <button type="button" className={`${styles.textButton} ${styles.destructive}`} onClick={() => setConfirming(subscription.id)}>{t.settings.removeScenario}</button>
                )}
              </div>
            </div>
          );
        })}
        <ScenarioPicker />
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>{t.settings.appearance}</h2>
        <label className={styles.rowField}>
          {t.settings.language}
          <select className={styles.select} value={localeValue} onChange={(e) => setLocale(e.target.value === "system" ? undefined : (e.target.value as Locale))}>
            <option value="system">{t.settings.languageSystem}</option>
            {LOCALES.map((code) => <option key={code} value={code}>{LOCALE_NAMES[code]}</option>)}
          </select>
        </label>
        {restartForPickers.data && <p className={styles.muted}>{t.settings.restartForPickers}</p>}
        <label className={styles.rowField}>
          {t.settings.theme}
          <select className={styles.select} value={settings.data?.theme ?? "system"} onChange={(e) => app.store.settings.update({ theme: e.target.value as ThemeSetting })}>
            <option value="system">{t.settings.themeSystem}</option>
            <option value="light">{t.settings.themeLight}</option>
            <option value="dark">{t.settings.themeDark}</option>
          </select>
        </label>
      </section>

      {settings.data && (settings.data.session ? <AccountSection session={settings.data.session} lastArchiveAt={settings.data.lastArchive?.at} /> : <SignInSection authError={authError} />)}

      <TransferSection openEntry={searchParams.get("transfer") === "1"} />

      <section className={styles.section}>
        <h2 className={styles.heading}>{t.settings.about}</h2>
        <div className={styles.card}>
          <p className={styles.muted}>{t.settings.version} {__APP_VERSION__} · {locale}</p>
          <p><a href={sitePageUrl("/privacy.html")} target="_blank" rel="noopener noreferrer">{t.settings.privacy}</a></p>
        </div>
      </section>
    </Screen>
  );
}
